import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAssignedContacts,
  updateAssignedContact,
  addAssignedContact,
  removeAssignedContact,
  setContactOptions,
  setContactsLoading,
} from "../../../../../redux/customers/customerContactsSlice";
import {
  fetchAssignedContacts,
  fetchAllContacts,
  updateContact,
  updateAssignedContacts as updateAssignedCustomerContactsService,
} from "../services/customerContactService";

const useCustomerContacts = (customerId) => {
  const dispatch = useDispatch();
  const assignedContacts =
    useSelector((state) => state.customerContacts.assignedContacts) || [];
  const contactOptions =
    useSelector((state) => state.customerContacts.contactOptions) || [];
  const loading = useSelector((state) => state.customerContacts.loading);

  const loadContacts = useCallback(async () => {
    if (!customerId) return;
    dispatch(setContactsLoading(true));
    try {
      const [allContacts, assigned] = await Promise.all([
        fetchAllContacts(),
        fetchAssignedContacts(customerId),
      ]);
      dispatch(
        setContactOptions(Array.isArray(allContacts) ? allContacts : [])
      );
      // Filter assigned contacts based on the three roles.
      const filteredAssigned = Array.isArray(assigned)
        ? assigned.filter(
            (item) =>
              item.mainContact || item.agentContact || item.collectionsContact
          )
        : [];
      const enrichedAssigned = filteredAssigned.map((assignedContact) => {
        const fullContact = allContacts.find(
          (c) => c.contactID === assignedContact.contactId
        );
        return fullContact
          ? {
              ...fullContact,
              ...assignedContact,
              contactID: assignedContact.contactId,
            }
          : assignedContact;
      });
      dispatch(setAssignedContacts(enrichedAssigned));
    } catch (error) {
      console.error("Error loading customer contacts:", error);
      dispatch(setAssignedContacts([]));
    } finally {
      dispatch(setContactsLoading(false));
    }
  }, [customerId, dispatch]);

  useEffect(() => {
    if (customerId) loadContacts();
  }, [customerId, loadContacts]);

  const handleRowFieldChange = useCallback(
    (contactID, field, value) => {
      dispatch(
        updateAssignedContact({ contactID, updates: { [field]: value } })
      );
    },
    [dispatch]
  );

  const handleRowAutoSave = useCallback(
    async (contactID) => {
      const contact = assignedContacts.find((c) => c.contactID === contactID);
      if (!contact || !contact.contactID) return;
      try {
        await updateContact(contactID, contact);
      } catch (error) {
        console.error("Error auto-saving customer contact:", error);
      }
    },
    [assignedContacts]
  );

  const addContactForRole = useCallback(
    async (roleKey, contactID) => {
      if (!contactID) {
        alert("Please select a contact to add.");
        return;
      }
      const selected = contactOptions.find(
        (c) => c.contactID.toString() === contactID.toString()
      );
      if (!selected) {
        console.error("Selected customer contact not found in options");
        return;
      }
      const existingContact = assignedContacts.find(
        (c) => c.contactID.toString() === contactID.toString()
      );
      if (existingContact) {
        if (existingContact[roleKey]) {
          alert(`Contact is already assigned as ${roleKey}.`);
          return;
        } else {
          const updatedContact = { ...existingContact, [roleKey]: true };
          dispatch(
            updateAssignedContact({
              contactID,
              updates: { [roleKey]: true },
            })
          );
          const updatedContacts = assignedContacts.map((c) =>
            c.contactID.toString() === contactID.toString() ? updatedContact : c
          );
          try {
            await updateAssignedCustomerContactsService(
              customerId,
              updatedContacts
            );
          } catch (error) {
            console.error(
              "Error updating assigned customer contacts on server:",
              error
            );
          }
          return;
        }
      }
      const newContact = {
        ...selected,
        customerId,
        mainContact: roleKey === "mainContact",
        agentContact: roleKey === "agentContact",
        collectionsContact: roleKey === "collectionsContact",
        isEditing: false,
        roleKey,
      };
      dispatch(addAssignedContact(newContact));
      try {
        await updateAssignedCustomerContactsService(customerId, [
          ...assignedContacts,
          newContact,
        ]);
      } catch (error) {
        console.error(
          "Error updating assigned customer contacts on server:",
          error
        );
      }
    },
    [contactOptions, assignedContacts, customerId, dispatch]
  );

  const removeContactForRole = useCallback(
    async (roleKey, contactID) => {
      const existing = assignedContacts.find((c) => c.contactID === contactID);
      if (!existing) return;
      const updatedContact = { ...existing, [roleKey]: false };
      if (
        !updatedContact.mainContact &&
        !updatedContact.agentContact &&
        !updatedContact.collectionsContact
      ) {
        dispatch(removeAssignedContact(contactID));
      } else {
        dispatch(
          updateAssignedContact({
            contactID,
            updates: { [roleKey]: false },
          })
        );
      }
      const updatedContacts = assignedContacts
        .map((c) => (c.contactID === contactID ? updatedContact : c))
        .filter((c) => c.mainContact || c.agentContact || c.collectionsContact);
      try {
        await updateAssignedCustomerContactsService(
          customerId,
          updatedContacts
        );
      } catch (error) {
        console.error(
          "Error updating assigned customer contacts on server:",
          error
        );
      }
    },
    [assignedContacts, customerId, dispatch]
  );

  return {
    assignedContacts,
    contactOptions,
    loading,
    handleRowFieldChange,
    handleRowAutoSave,
    addContactForRole,
    removeContactForRole,
  };
};

export default useCustomerContacts;
